import { Suspense, lazy, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  BlockTitle,
  Block,
  Button,
  Icon,
  Link,
  Navbar,
  NavLeft,
  NavTitle,
  Page,
  Panel,
  Tab,
  Tabs,
  Toolbar,
  ToolbarPane,
  View,
} from "framework7-react";
import { requests, requestStates } from "../constants/requests";
import { MenuList, MenuSection } from "../components/menu";
import ActivityButton from "../features/activity/activity-button";
import ActivityPopup from "../features/activity/activity-popup";
import AssignMePopup from "../features/assign-me/assign-me-popup";
import {
  clearAssignmentPrompt,
  promptAssignment,
  useAssignmentPrompt,
} from "../features/assign-me/assign-prompt";
import MeButton from "../features/assign-me/me-button";
import CreateGroupPopup from "../features/create-group-popup";
import RenameParticipantPopup from "../features/rename-participant-popup";
import ExpenseOverview from "../features/expense-overview";
import ExpensePopup from "../features/expense-popup";
import AppMenu from "../features/app-menu";
import GroupActions from "../features/group-actions";
import GroupList from "../features/group-list";
import GroupOverview from "../features/group-overview";
import HomeFab from "../features/home-fab";
import InstallSheet from "../features/install/install-sheet";
import JoinGroupPopup from "../features/join-group-popup";
import NotificationPrompt from "../features/notifications/notification-prompt";
import NotificationsButton from "../features/notifications/notifications-button";
import NotificationsSheet from "../features/notifications/notifications-sheet";
import PaymentPopup from "../features/payment-popup";
import RecurringButton from "../features/recurring/recurring-button";
import RecurringPopup from "../features/recurring/recurring-popup";
import SettlementPopup from "../features/settlement/settlement-popup";
import { useReportFilter } from "../features/report/use-report-filter";
import TagsPopup from "../features/tags/tags-popup";
import RedeemPopup from "../features/session-transfer/redeem-popup";
import TransferPrompt from "../features/session-transfer/transfer-prompt";
import TransferSheet from "../features/session-transfer/transfer-sheet";
import { useIsPending } from "../hooks/use-is-pending";
import { useAppBadge } from "../hooks/use-app-badge";
import { useBackClose } from "../hooks/use-back-close";
import { useLaunchAction } from "../hooks/use-launch-action";
import { usePopup } from "../hooks/use-popup";
import { useToast } from "../hooks/use-toast";
import { noop } from "../redux/api";
import {
  getGroupStatus,
  refreshGroups,
  selectCurrentGroup,
  selectCurrentGroupId,
  selectGroups,
  selectRequestState,
  showGroup,
} from "../redux/reducer/app-slice";
import { confirmDialog } from "../utils/confirm-dialog";
import { launchActions } from "../utils/launch-action";

const ADD_PARTICIPANTS = "participants";

const ReportTab = lazy(() => import("../features/report/report-tab"));

const toExpenseDraft = ({ name, amount }) => ({
  name,
  amount_in_decimals: amount,
});

const isSettled = (state) =>
  state === requestStates.success || state === requestStates.error;

const selectStartupSettled = (state) =>
  !selectCurrentGroupId(state) ||
  (isSettled(selectRequestState(state, requests.loadGroupStatus)) &&
    isSettled(selectRequestState(state, requests.loadGroups)));

// Popup rule: stable keys on <Page> children, popups only in .home-popups (AGENTS.md).
const Home = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPageShown, setIsPageShown] = useState(false);
  const expensePopup = usePopup();
  const editExpensePopup = usePopup();
  const paymentPopup = usePopup();
  const joinPopup = usePopup();
  const groupPopup = usePopup(false);
  const renamePopup = usePopup();
  const redeemPopup = usePopup();
  const transferSheet = usePopup();
  const assignPopup = usePopup();
  const notificationsSheet = usePopup();
  const [isReportActive, setIsReportActive] = useState(false);
  const [wasReportShown, setWasReportShown] = useState(false);
  const tagsPopup = usePopup();
  const activityPopup = usePopup();
  const recurringPopup = usePopup();
  const settlementPopup = usePopup();
  const releaseMenuBack = useBackClose(isMenuOpen, () => setIsMenuOpen(false));

  const dispatch = useDispatch();
  const { showError } = useToast();

  const groups = useSelector(selectGroups);
  const group = useSelector(selectCurrentGroup);
  const hasGroup = Boolean(group?.id);
  const hasGroups = groups.length > 0;
  const hasParticipants = group?.participants?.length > 0;
  const participants = group?.participants || [];
  const isLoading = useIsPending(requests.loadGroupStatus);
  const startupSettled = useSelector(selectStartupSettled);
  const promptGroupId = useAssignmentPrompt();
  const [reportFilter, setReportFilter] = useReportFilter(group?.id);

  useEffect(() => {
    if (!hasParticipants) setIsReportActive(false);
  }, [hasParticipants]);

  const groupId = group?.id;
  const groupMe = group?.me;
  const openAssignPopup = assignPopup.open;
  useEffect(() => {
    if (!isPageShown || promptGroupId === null) return;
    if (`${promptGroupId}` !== `${groupId}`) return;
    if (groupMe != null) clearAssignmentPrompt();
    else openAssignPopup("welcome");
  }, [isPageShown, promptGroupId, groupId, groupMe, openAssignPopup]);

  const settlementGroupId = settlementPopup.data;
  const closeSettlement = settlementPopup.close;
  useEffect(() => {
    if (`${settlementGroupId}` !== `${groupId}` || !hasParticipants) {
      closeSettlement();
    }
  }, [settlementGroupId, groupId, hasParticipants, closeSettlement]);

  const openGroup = (groupId) => {
    if (!groups.some((g) => g.id === groupId)) return;
    setIsMenuOpen(false);
    const silent = group?.id === groupId;
    dispatch(getGroupStatus(groupId, silent, noop, showError));
  };

  const openEditGroup = () => groupPopup.open(true);
  const openAddParticipants = () => groupPopup.open(ADD_PARTICIPANTS);

  const showAddParticipantsHint = () =>
    confirmDialog({
      title: "Noch keine Personen",
      text: "Füge zuerst Personen hinzu, dann kannst du Ausgaben, Zahlungen und Einnahmen erfassen.",
      confirmText: "Hinzufügen",
      cssClass: "add-participants-dialog",
      onConfirm: openAddParticipants,
    });

  const addExpense = (draft = null) => {
    if (hasParticipants) expensePopup.open(draft);
    else showAddParticipantsHint();
  };

  const addPayment = () => {
    if (hasParticipants) paymentPopup.open(null);
    else showAddParticipantsHint();
  };

  useAppBadge();
  useLaunchAction({
    ready: startupSettled,
    hasGroup,
    onLaunch: ({ action, draft, groupId }) => {
      if (action === launchActions.openGroup) openGroup(groupId);
      else if (action === launchActions.newPayment) addPayment();
      else addExpense(draft ? toExpenseDraft(draft) : null);
    },
  });

  const handleRefresh = (done) => {
    dispatch(refreshGroups());
    if (!hasGroup) {
      done();
      return;
    }
    dispatch(
      getGroupStatus(group.id, false, done, (message) => {
        done();
        showError(message);
      }),
    );
  };

  const openCreateGroup = () => groupPopup.open(false);

  const openJoinGroup = () => {
    setIsMenuOpen(false);
    joinPopup.open();
  };

  const openTransfer = () => {
    setIsMenuOpen(false);
    transferSheet.open();
  };

  const openAssignMe = () => {
    setIsMenuOpen(false);
    assignPopup.open("menu");
  };

  const openActivity = () => {
    setIsMenuOpen(false);
    activityPopup.open(group.id);
  };

  const openRecurring = () => {
    setIsMenuOpen(false);
    recurringPopup.open(group.id);
  };

  const openNotifications = () => {
    setIsMenuOpen(false);
    notificationsSheet.open(group);
  };

  return (
    <Page
      ptr
      ptrMousewheel={true}
      onPtrRefresh={handleRefresh}
      // F7 fails to open a popup on a page that is still being inserted.
      onPageAfterIn={() => setIsPageShown(true)}
    >
      <Navbar key="navbar">
        <NavLeft>
          <Link iconOnly aria-label="Menü" onClick={() => setIsMenuOpen(true)}>
            <Icon ios="f7:menu" md="material:menu" />
          </Link>
        </NavLeft>
        <NavTitle
          className={isLoading ? "skeleton-text skeleton-effect-wave" : ""}
        >
          {group?.name || "Home"}
        </NavTitle>
      </Navbar>
      <Panel
        key="panel"
        swipe
        push
        left
        opened={isMenuOpen}
        onPanelOpen={() => setIsMenuOpen(true)}
        onPanelClose={releaseMenuBack}
        onPanelClosed={() => setIsMenuOpen(false)}
      >
        <View>
          <Page>
            <Navbar title="Split It" />
            <GroupList
              groups={groups}
              closeParentPanel={() => setIsMenuOpen(false)}
              onCreate={() => {
                setIsMenuOpen(false);
                openCreateGroup();
              }}
              onJoin={openJoinGroup}
            />
            {hasGroup && (
              <MenuSection title={group.name} className="group-menu">
                <MenuList>
                  <MeButton onClick={openAssignMe} />
                  <NotificationsButton onClick={openNotifications} />
                  <RecurringButton onClick={openRecurring} />
                  <ActivityButton onClick={openActivity} />
                </MenuList>
              </MenuSection>
            )}
            <AppMenu
              showTransfer={hasGroups}
              onTransfer={openTransfer}
              onInstall={() => setIsMenuOpen(false)}
            />
          </Page>
        </View>
      </Panel>
      {hasParticipants && (
        <Toolbar key="tabbar" tabbar top>
          <ToolbarPane>
            <Link tabLink="#overview" tabLinkActive>
              Übersicht
            </Link>
            <Link tabLink="#expenses">Ausgaben</Link>
            <Link tabLink="#report">Auswertung</Link>
          </ToolbarPane>
        </Toolbar>
      )}
      {hasParticipants && (
        <Tabs key="tabs" swipeable>
          <Tab id="overview" tabActive>
            <GroupOverview
              group={group}
              onChooseMe={() => assignPopup.open("menu")}
              onOpenSettlement={() => settlementPopup.open(group.id)}
            />
          </Tab>
          <Tab id="expenses">
            <ExpenseOverview
              group={group}
              onOpenExpense={(expense) => editExpensePopup.open(expense)}
              onOpenPayment={(payment) => paymentPopup.open(payment)}
              onManageTags={() => tagsPopup.open()}
            />
          </Tab>
          <Tab
            id="report"
            onTabShow={() => {
              setIsReportActive(true);
              setWasReportShown(true);
            }}
            onTabHide={() => setIsReportActive(false)}
          >
            {wasReportShown && (
              <Suspense fallback={null}>
                <ReportTab
                  group={group}
                  active={isReportActive}
                  filter={reportFilter}
                  onFilterChange={setReportFilter}
                  onError={showError}
                />
              </Suspense>
            )}
          </Tab>
        </Tabs>
      )}
      {hasGroup && !hasParticipants && (
        <div key="no-participants" className="home-empty-state no-participants">
          <BlockTitle large>Noch keine Personen</BlockTitle>
          <Block>
            <p>
              Füge zuerst Personen hinzu, dann kannst du Ausgaben, Zahlungen und
              Einnahmen erfassen.
            </p>
          </Block>
          <Block className="group-actions">
            <Button fill large onClick={openAddParticipants}>
              Personen hinzufügen
            </Button>
          </Block>
        </div>
      )}
      {!hasGroup && (
        <div key="empty-state" className="home-empty-state">
          <BlockTitle large>Willkommen!</BlockTitle>
          <Block>
            <p>
              Du bist noch in keiner Gruppe. Erstelle eine neue Gruppe oder
              tritt mit einem Einladungs-Code einer bestehenden Gruppe bei.
            </p>
          </Block>
          <GroupActions
            large
            onCreate={openCreateGroup}
            onJoin={openJoinGroup}
          />
          {!hasGroups && (
            <TransferPrompt onEnterCode={() => redeemPopup.open()} />
          )}
        </div>
      )}
      {hasGroup && (
        <HomeFab
          key="fab"
          slot="fixed"
          onEditGroup={openEditGroup}
          onAddPayment={addPayment}
          onAddExpense={() => addExpense()}
        />
      )}
      <div key="popups" className="home-popups">
        <ExpensePopup
          autoFocus
          participants={participants}
          expense={expensePopup.data}
          opened={expensePopup.opened}
          onClose={expensePopup.close}
        />
        <ExpensePopup
          participants={participants}
          expense={editExpensePopup.data}
          opened={editExpensePopup.opened}
          onClose={editExpensePopup.close}
        />
        <PaymentPopup
          participants={participants}
          payment={paymentPopup.data}
          opened={paymentPopup.opened}
          onClose={paymentPopup.close}
        />
        <JoinGroupPopup
          opened={joinPopup.opened}
          onClose={joinPopup.close}
          onJoined={(joinedGroup) => {
            joinPopup.close();
            promptAssignment(joinedGroup.id);
            dispatch(showGroup(joinedGroup));
            dispatch(refreshGroups());
          }}
        />
        <CreateGroupPopup
          opened={groupPopup.opened}
          onClose={() => {
            groupPopup.close();
            setIsMenuOpen(false);
          }}
          isCurrentGroup={Boolean(groupPopup.data)}
          startAtParticipants={groupPopup.data === ADD_PARTICIPANTS}
          onRenameParticipant={(participant) => renamePopup.open(participant)}
        />
        <RenameParticipantPopup
          opened={renamePopup.opened}
          participant={renamePopup.data}
          onClose={renamePopup.close}
        />
        <RedeemPopup
          opened={redeemPopup.opened}
          onClose={redeemPopup.close}
          onRedeemed={redeemPopup.close}
        />
        <TransferSheet
          opened={transferSheet.opened}
          onClose={transferSheet.close}
        />
        <AssignMePopup
          opened={assignPopup.opened}
          welcome={assignPopup.data === "welcome"}
          onDone={assignPopup.close}
          onClose={() => {
            clearAssignmentPrompt();
            assignPopup.close();
          }}
        />
        <NotificationsSheet
          group={notificationsSheet.data}
          opened={notificationsSheet.opened}
          onClose={notificationsSheet.close}
          onChooseMe={() => {
            notificationsSheet.close();
            assignPopup.open("menu");
          }}
        />
        <TagsPopup opened={tagsPopup.opened} onClose={tagsPopup.close} />
        <ActivityPopup
          groupId={activityPopup.data}
          opened={activityPopup.opened}
          onClose={activityPopup.close}
        />
        <RecurringPopup
          groupId={recurringPopup.data}
          participants={participants}
          opened={recurringPopup.opened}
          onClose={recurringPopup.close}
        />
        <SettlementPopup
          group={group}
          groupId={settlementPopup.data}
          opened={settlementPopup.opened}
          onClose={settlementPopup.close}
        />
        <NotificationPrompt />
        <InstallSheet />
      </div>
    </Page>
  );
};

export default Home;
